<script setup>
import { defineProps, defineEmits, ref, computed, watch } from 'vue';

const props = defineProps({
  label: { type: String, default: 'Select Values' },
  numberOptions: { type: Array, default: () => [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] },
  modelValue: Object
});

const emit = defineEmits(['update:modelValue']);

const filterOptions = [
  { text: 'Multiple Values', value: 'multiple' },
  { text: 'Equal to', value: 'equal' },
  { text: 'Greater than', value: 'greater_than' },
  { text: 'Less than', value: 'less_than' },
  { text: 'Between', value: 'between' },
];

const filterType = ref(props.modelValue?.type || 'between');
const numberValue = ref(props.modelValue?.value || []);
const secondValue = ref(props.modelValue?.value2 || null);

const filterData = computed(() => {
  if (filterType.value === 'between') {
    return { type: filterType.value, value: numberValue.value, value2: secondValue.value };
  }
  return { type: filterType.value, value: numberValue.value };
});

const multiSelectLabel = computed(() => 
  numberValue.value.length ? `${numberValue.value.length} selected` : props.label
);

const updateFilter = () => emit('update:modelValue', filterData.value);

watch(filterType, (newType) => {
  numberValue.value = newType === 'multiple' ? [] : '';
  secondValue.value = null;
});
</script>

<template>
  <v-sheet class="filter-card">
    <span class="filter-card-label">{{ label }}</span>
    <v-row dense>
      <v-col cols="12">
        <v-select
          v-model="filterType"
          :items="filterOptions"
          item-title="text"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          class="full-width"
          @update:model-value="updateFilter"
        ></v-select>
      </v-col>

      <v-col cols="12" v-if="filterType === 'multiple'">
        <v-combobox
          v-model="numberValue"
          :items="props.numberOptions"
          multiple
          density="compact"
          variant="outlined"
          hide-details
          :menu-props="{ closeOnContentClick: false }"
          @update:model-value="updateFilter"
          class="full-width"
          placeholder="Select"
        >
          <template v-slot:selection="{ index }">
            <span v-if="index === 0">{{ multiSelectLabel }}</span>
          </template>
        </v-combobox>
      </v-col>

      <v-col cols="12" v-if="filterType !== 'multiple' && filterType !== 'between'">
        <v-text-field
          v-model="numberValue"
          type="text"
          density="compact"
          variant="outlined"
          hide-details
          @input="updateFilter"
          class="full-width"
          :placeholder="label"
        ></v-text-field>
      </v-col>

      <v-col cols="6" v-if="filterType === 'between'">
        <v-text-field
          v-model="numberValue"
          type="text"
          density="compact"
          variant="outlined"
          hide-details
          @input="updateFilter"
          class="full-width"
          placeholder="Min Value"
        ></v-text-field>
      </v-col>

      <v-col cols="6" v-if="filterType === 'between'">
        <v-text-field
          v-model="secondValue"
          type="text"
          density="compact"
          variant="outlined"
          hide-details
          @input="updateFilter"
          class="full-width"
          placeholder="Max Value"
        ></v-text-field>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<style scoped>
.full-width {
  width: 100%;
}
</style>